import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewRef } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { Options } from 'ngx-slider-v2';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AppStateService } from 'src/app/core-module/services/app-state.service';
import { AloAocStateService } from '../../../core-module/services/state/alo-aoc-state.service';
import { CheckboxStateService } from '../../../core-module/services/state/checkbox-state.service';
import { FeatureDefaultsStateService } from '../../../core-module/services/state/feature-defaults-state.service';
import { FeatureFlagsStateService } from '../../../core-module/services/state/feature-flags-state.service';
import { GraphStateService } from '../../../core-module/services/state/graph-state.service';
import { InputErrorsStateService } from '../../../core-module/services/state/input-errors-state.service';

const OTP_VALIDATION_ERROR_MESSAGE = 'The OTP feature is incompletely stored in the driver. Please re-configure.';

@Component({
  standalone: false,
  selector: 'app-otp',
  templateUrl: './otp.component.html',
  styleUrls: ['./otp.component.scss']
})
export class OtpComponent extends FieldType implements OnInit, OnDestroy {

  // Selection state (OTPMode: 1 = FTP, 0 = DTP)
  ftpEnabled = true;
  dtpEnabled = false;

  ftpOption = 'dlmFlex';
  dtpOption = '83to87';

  // Slider model + display values
  ftpResistanceMin = 1000;
  ftpResistanceMax = 15000;
  ftpStartNtc: number = 9693;
  ftpStopNtc: number  = 8480;
  ftpStartNtcDisplay: number | string = 9693;
  ftpStopNtcDisplay: number | string  = 8480;
  ftpStartTempLabel = '83';
  ftpStopTempLabel  = '87';

  ftpStartDimOptions: Options = { floor: 1000, ceil: 15000, showSelectionBar: true };
  ftpStopDimOptions: Options  = { floor: 1000, ceil: 15000, showSelectionBar: true };

  // Include-in-configuration checkbox state
  isIncludedInConfiguration = false;
  private ignoreParentUpdate = false;
  private hasConfigData = false;

  // Error tracking
  inputError: { [key: string]: boolean } = {};
  hasInputError = false;
  otpDisabledButton = false;
  hasOtpValidationError = false;
  readonly otpValidationMessage = OTP_VALIDATION_ERROR_MESSAGE;

  // Standard/Advanced mode
  quickConfigvalue: boolean;
  quickConfigCustomCSS: string;
  setQuickConfig: string;

  // Default-color tracking (drives Restore Default button)
  defaultColor = true;

  // Schema-derived defaults
  private otpModeDefault   = 1;
  private ftpOptionDefault = 'dlmFlex';
  private dtpOptionDefault = '83to87';
  private dimStartDefault  = 9693;
  private dimStopDefault   = 8480;
  // Minimum dim level (%) reached when NTC resistance is at/below DimStop.
  // Sourced from the schema (DimStop.minDimLevel); default 10 matches
  // the historical FTP curve plateau.
  private dimStopMinDimLevel = 10;

  private isResetting = false;
  private properties: any;

  // Exposed to template for getSliderSafeValue calls
  dimStartMin = 1000;
  dimStartMax = 15000;
  dimStopMin  = 1000;
  dimStopMax  = 15000;

  private readonly dimStartInput$ = new Subject<string>();
  private readonly dimStopInput$  = new Subject<string>();
  private readonly destroy$       = new Subject<void>();

  constructor(
    private readonly ref: ChangeDetectorRef,
    private readonly appStateService: AppStateService,
    private readonly featureDefaults: FeatureDefaultsStateService,
    private readonly featureFlags: FeatureFlagsStateService,
    private readonly checkboxState: CheckboxStateService,
    private readonly inputErrors: InputErrorsStateService,
    private readonly graphState: GraphStateService,
    private readonly aloAoc: AloAocStateService
  ) {
    super();
  }

  ngOnInit(): void {
    // Protect initial load from parent checkbox updates
    this.ignoreParentUpdate = true;

    this.properties = (this.field as any)?.properties;

    if (this.properties) {
      this.loadFromSchema();
    }

    this.ftpStartNtcDisplay = this.ftpStartNtc;
    this.ftpStopNtcDisplay  = this.ftpStopNtc;

    this.updateTempLabels();
    this.pushFtpGraph();
    this.pushDtpGraph();
    this.setupInputDebouncing();

    // Load saved configuration data (matching LSI pattern)
    let savedValue: any = null;
    let isConfigEdited = false;

    // Check UnSelectedFeatConfig first (for edited but unselected features)
    const unSelectedFeat = this.appStateService.getUnSelectedFeatConfig();
    if (unSelectedFeat != null && Array.isArray(unSelectedFeat)) {
      let featIndex = 0;
      unSelectedFeat.forEach((element) => {
        if (element['featureName'] && element['featureName'].toString().toUpperCase() === 'OTP') {
          isConfigEdited = true;
          savedValue = element['featureParameters'][0];
          unSelectedFeat.splice(featIndex, 1);
          this.appStateService.setUnSelectedFeatConfig(unSelectedFeat);
        } else {
          featIndex++;
        }
      });
    }

    // Use centralized method to get feature data (prioritizes SaveSetData over ConfigurationData)
    if (!isConfigEdited) {
      // Check saveReadValue first — this is the actual device state after a read.
      // Consume and clear it so subsequent re-inits use the user-configured saveSetData instead.
      const saveReadValue = this.appStateService.getSaveReadValue();
      if (saveReadValue?.OTP && Object.keys(saveReadValue.OTP).length > 0) {
        savedValue = saveReadValue.OTP;
        this.hasConfigData = true;
        // Clear OTP from saveReadValue so it is only consumed once
        delete saveReadValue.OTP;
        this.appStateService.setSaveReadValue(Object.keys(saveReadValue).length > 0 ? saveReadValue : null);
      } else {
        // Fall back to saveSetData (user-configured values or values synced from previous read)
        const otpData = this.appStateService.getFeatureData('OTP');
        if (otpData) {
          savedValue = otpData;
          this.hasConfigData = Object.keys(savedValue).length > 0;
        }
      }
    }

    // Load values from saved data
    if (savedValue && Object.keys(savedValue).length > 0) {
      this.loadDataFromObject(savedValue);
    }

    this.updateFormControl();

    this.featureDefaults.setOtpDefaultColor(true);

    this.featureDefaults.getOtpDefaultValue
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response === 'valid') {
          this.resetToDefaults();
          this.featureDefaults.setOtpDefaultValue('');
        }
      });

    this.checkboxState.getOtpParentCheckbox
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state !== null && !this.ignoreParentUpdate) {
          this.isIncludedInConfiguration = state.checked;

          if (!state.checked) {
            this.hasConfigData = false;
            this.inputErrors.setOtpInputError.next(false);
            this.ref.detectChanges();
            return;
          }

          // Refresh flow: if data already loaded, just send current error state
          if (this.hasConfigData) {
            this.sendErrorStateToParent();
            return;
          }

          this.updateFormControl();
          this.sendErrorStateToParent();
          this.ref.detectChanges();
        }
      });

    this.revalidateAllFields();
    this.checkDefaults();

    // Enable checkbox if any value is non-default (import DCF / find-and-select flow)
    if (!this.defaultColor) {
      this.checkboxState.enableOtpCheckbox();
      this.isIncludedInConfiguration = true;
      this.hasConfigData = true;
    }

    // Send error state AFTER isIncludedInConfiguration is set
    this.sendErrorStateToParent();

    // Subscribe to Standard/Advanced mode changes
    this.aloAoc.getquickConfig
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data === 'quickConfig') {
          this.quickConfigvalue = true;
          this.quickConfigCustomCSS = 'quick-custom-inputbox textBoxUnit';
          this.setQuickConfig = 'quickConfig';
        } else if (data === 'standard') {
          this.quickConfigvalue = false;
          this.quickConfigCustomCSS = 'custom-inputbox';
          this.setQuickConfig = 'standard';
        }
        setTimeout(() => {
          if (this.ref && !(this.ref as ViewRef).destroyed) {
            this.ref.detectChanges();
          }
        });
      });

    this.featureFlags.getOtpFeatApi
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        if (!data) { return; }

        const ftp = data?.FixtureTemperatureProtection;
        const dtp = data?.DriverTemperatureProtection;

        if (ftp) {
          this.ftpOption = ftp.SelectedNtc === 'DLMFlexCompatible' ? 'dlmFlex' : 'customNtc';
          this.ftpStartNtc = this.ftpStartNtcDisplay = this.getNumber(ftp.StartNtc, this.ftpStartNtc);
          this.ftpStopNtc  = this.ftpStopNtcDisplay  = this.getNumber(ftp.StopNtc,  this.ftpStopNtc);
          this.updateTempLabels();
          this.pushFtpGraph();
        }

        if (dtp) {
          const start = this.getNumber(dtp.StartTemp, 83);
          this.dtpOption = start === 92 ? '92to97' : '83to87';
          this.pushDtpGraph();
        }

        const mode = data?.OTPMode;
        if (mode !== undefined && mode !== null) {
          this.ftpEnabled = (Number(mode) === 1);
          this.dtpEnabled = (Number(mode) === 0);
        }

        this.checkDefaults();
        this.updateFormControl();
        this.ref.detectChanges();
      });

    // Allow parent updates after initialization
    this.ignoreParentUpdate = false;
  }

  private loadDataFromObject(data: any): void {
    this.hasOtpValidationError = this.hasOtpCrcValidationError(data);
    this.applyOtpModeFromLoadedData(data);
    this.applyOtpParametersFromLoadedData(data);

    this.updateTempLabels();
    this.pushFtpGraph();
    this.pushDtpGraph();
    this.recalcErrorState();
  }

  private applyOtpModeFromLoadedData(data: any): void {
    if (this.hasOtpValidationError) {
      // CRC-corrupt OTP payload is not safely interpretable as FTP/DTP mode.
      // Keep both modes unchecked and locked until restore default.
      this.ftpEnabled = false;
      this.dtpEnabled = false;
      return;
    }

    if (data.OTPMode !== undefined) {
      this.ftpEnabled = (Number(data.OTPMode) === 1);
      this.dtpEnabled = !this.ftpEnabled;
      return;
    }

    this.applyInferredOtpMode(data);
  }

  private applyInferredOtpMode(data: any): void {
    // Infer mode from which fields are present (partial read response)
    const hasFtpFields = data.SelectedNTC !== undefined || data.DimStart !== undefined || data.DimStop !== undefined;
    const hasDtpFields = data.DTP !== undefined;
    if (hasDtpFields && !hasFtpFields) {
      // Only DTP data present → device is in DTP mode
      this.ftpEnabled = false;
      this.dtpEnabled = true;
    } else if (hasFtpFields && !hasDtpFields) {
      // Only FTP data present → device is in FTP mode
      this.ftpEnabled = true;
      this.dtpEnabled = false;
    }
  }

  private applyOtpParametersFromLoadedData(data: any): void {
    if (data.SelectedNTC !== undefined) {
      this.ftpOption = data.SelectedNTC === 'DLM Flex Compatible' ? 'dlmFlex' : 'customNtc';
      const disabled = this.ftpOption === 'dlmFlex';
      this.ftpStartDimOptions = { ...this.ftpStartDimOptions, disabled };
      this.ftpStopDimOptions  = { ...this.ftpStopDimOptions,  disabled };
    }

    if (data.DimStart !== undefined) {
      this.ftpStartNtc = this.ftpStartNtcDisplay = data.DimStart;
    }

    if (data.DimStop !== undefined) {
      this.ftpStopNtc = this.ftpStopNtcDisplay = data.DimStop;
    }

    if (data.DTP !== undefined) {
      this.dtpOption = data.DTP === '92-97' ? '92to97' : '83to87';
    }
  }

  private hasOtpCrcValidationError(data: any): boolean {
    if (!data || typeof data !== 'object') {
      return false;
    }

    const pending: any[] = [data];

    while (pending.length > 0) {
      const node = pending.shift();
      if (!node || typeof node !== 'object') {
        continue;
      }

      const otpValidationError = node._otpValidationError ?? node.otpValidationError;
      if (typeof otpValidationError === 'string' && otpValidationError.trim().length > 0) {
        return true;
      }

      const descriptions = Array.isArray(node.descriptionList) ? node.descriptionList : [];
      const hasCrcDescription = descriptions.some((description: unknown) => {
        if (typeof description !== 'string') {
          return false;
        }

        const normalizedDescription = description.toLowerCase();
        return normalizedDescription.includes('_otpvalidationerror')
          || normalizedDescription.includes('otp payload is invalid')
          || normalizedDescription.includes('crc check failed');
      });
      if (hasCrcDescription) {
        return true;
      }

      Object.values(node).forEach((value: unknown) => {
        if (value && typeof value === 'object') {
          pending.push(value);
        }
      });
    }

    return false;
  }

  private revalidateAllFields(): void {
    // Revalidate cross-field constraint after loading data
    if (this.ftpEnabled && this.ftpOption === 'customNtc') {
      const startOk = this.ftpStartNtc >= this.dimStartMin && this.ftpStartNtc <= this.dimStartMax;
      const stopOk  = this.ftpStopNtc  >= this.dimStopMin  && this.ftpStopNtc  <= this.dimStopMax;

      // Out-of-range errors take priority over cross-field errors
      if (!startOk) {
        this.inputError['dimStart'] = true;
      }
      if (!stopOk) {
        this.inputError['dimStop'] = true;
      }

      // Cross-field: start must be greater than stop (higher resistance = lower temp)
      if (startOk && stopOk) {
        this.inputError['dimStart'] = (this.ftpStartNtc <= this.ftpStopNtc);
        this.inputError['dimStop']  = (this.ftpStopNtc  >= this.ftpStartNtc);
      }
      this.recalcErrorState();
    }
  }

  private loadFromSchema(): void {
    const otpMode  = this.properties['OTPMode'];
    const dimStart = this.properties['DimStart'];
    const dimStop  = this.properties['DimStop'];
    const dtp      = this.properties['DTP'];
    const ntc      = this.properties['SelectedNTC'] ?? this.properties['SelectedNtc'];

    this.otpModeDefault = otpMode?.default ?? 1;
    this.ftpEnabled = (this.otpModeDefault === 1);
    this.dtpEnabled = (this.otpModeDefault === 0);

    this.dimStartMin     = dimStart?.minimum ?? 1000;
    this.dimStartMax     = dimStart?.maximum ?? 15000;
    this.dimStartDefault = dimStart?.default ?? 9693;
    this.ftpStartNtc     = this.dimStartDefault;

    this.dimStopMin     = dimStop?.minimum ?? 1000;
    this.dimStopMax     = dimStop?.maximum ?? 15000;
    this.dimStopDefault = dimStop?.default ?? 8480;
    this.ftpStopNtc     = this.dimStopDefault;
    // Schema-driven low-temperature dim level (%) used by the FTP graph.
    this.dimStopMinDimLevel = typeof dimStop?.minDimLevel === 'number' ? dimStop.minDimLevel : 10;

    this.ftpResistanceMin = Math.min(this.dimStartMin, this.dimStopMin);
    this.ftpResistanceMax = Math.max(this.dimStartMax, this.dimStopMax);

    this.ftpStartDimOptions = { floor: this.dimStartMin, ceil: this.dimStartMax, showSelectionBar: true };
    this.ftpStopDimOptions  = { floor: this.dimStopMin,  ceil: this.dimStopMax,  showSelectionBar: true };

    const ntcDefault = ntc?.default ?? 'DLM Flex Compatible';
    this.ftpOptionDefault = ntcDefault === 'DLM Flex Compatible' ? 'dlmFlex' : 'customNtc';
    this.ftpOption = this.ftpOptionDefault;

    // Disable sliders when default is DLM Flex
    if (this.ftpOption === 'dlmFlex') {
      this.ftpStartDimOptions = { ...this.ftpStartDimOptions, disabled: true };
      this.ftpStopDimOptions  = { ...this.ftpStopDimOptions,  disabled: true };
    }

    const dtpDefault = dtp?.default ?? '83-87';
    this.dtpOptionDefault = dtpDefault === '92-97' ? '92to97' : '83to87';
    this.dtpOption = this.dtpOptionDefault;
  }

  private setupInputDebouncing(): void {
    this.dimStartInput$.pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(v => this.applyInputValue(v, 'dimStart'));
    this.dimStopInput$.pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(v => this.applyInputValue(v, 'dimStop'));
  }

  ftpStartNtcChange(value: number): void {
    this.ignoreParentUpdate = true;
    this.inputError['dimStart'] = false;
    if (value >= this.dimStartMin && value <= this.dimStartMax) {
      this.ftpStartNtc = value;
      this.ftpStartNtcDisplay = value;
      // Cross-field: DimStart must be strictly > DimStop
      this.inputError['dimStart'] = (value <= this.ftpStopNtc);
      // If constraint is now satisfied, also clear stop's cross-field error (if stop is in range)
      if (value > this.ftpStopNtc && this.ftpStopNtc >= this.dimStopMin && this.ftpStopNtc <= this.dimStopMax) {
        this.inputError['dimStop'] = false;
      }
      this.updateTempLabels();
      this.pushFtpGraph();
      this.recalcErrorState();
      this.checkDefaults();
      this.updateFormControl();
      if (!this.defaultColor) {
        this.checkboxState.enableOtpCheckbox();
        this.isIncludedInConfiguration = true;
      }
      this.sendErrorStateToParent();
      this.ref.detectChanges();
    }
    this.ignoreParentUpdate = false;
  }

  ftpStopNtcChange(value: number): void {
    this.ignoreParentUpdate = true;
    this.inputError['dimStop'] = false;
    if (value >= this.dimStopMin && value <= this.dimStopMax) {
      this.ftpStopNtc = value;
      this.ftpStopNtcDisplay = value;
      // Cross-field: DimStop must be strictly < DimStart
      this.inputError['dimStop'] = (value >= this.ftpStartNtc);
      // If constraint is now satisfied, also clear start's cross-field error (if start is in range)
      if (value < this.ftpStartNtc && this.ftpStartNtc >= this.dimStartMin && this.ftpStartNtc <= this.dimStartMax) {
        this.inputError['dimStart'] = false;
      }
      this.updateTempLabels();
      this.pushFtpGraph();
      this.recalcErrorState();
      this.checkDefaults();
      this.updateFormControl();
      if (!this.defaultColor) {
        this.checkboxState.enableOtpCheckbox();
        this.isIncludedInConfiguration = true;
      }
      this.sendErrorStateToParent();
      this.ref.detectChanges();
    }
    this.ignoreParentUpdate = false;
  }

  onFieldInput(value: string, type: string): void {
    if (type === 'dimStart') { this.dimStartInput$.next(value); }
    else if (type === 'dimStop') { this.dimStopInput$.next(value); }
  }

  onFieldBlur(inputEl: HTMLInputElement, type: string): void {
    const num = parseFloat(inputEl.value);
    const cleaned = isNaN(num) ? '' : String(num);
    inputEl.value = '';
    inputEl.value = cleaned;
    if (type === 'dimStart') { this.ftpStartNtcDisplay = isNaN(num) ? '' : num; }
    else if (type === 'dimStop') { this.ftpStopNtcDisplay = isNaN(num) ? '' : num; }
  }

  private applyInputValue(value: string, type: string): void {
    if (this.isResetting) { return; }

    const num = parseInt(value, 10);
    const min = type === 'dimStart' ? this.dimStartMin : this.dimStopMin;
    const max = type === 'dimStart' ? this.dimStartMax : this.dimStopMax;

    if (type === 'dimStart') {
      this.ftpStartNtc = num;
      this.ftpStartNtcDisplay = num;
    } else {
      this.ftpStopNtc = num;
      this.ftpStopNtcDisplay = num;
    }

    if (value === '' || isNaN(num) || num < min || num > max) {
      this.inputError[type] = true;
    } else {
      // Cross-field check: only the changed field gets the error
      if (type === 'dimStart') {
        this.inputError['dimStart'] = (num <= this.ftpStopNtc);
        // Constraint satisfied — also clear stop's cross-field error if stop is in range
        if (num > this.ftpStopNtc && this.ftpStopNtc >= this.dimStopMin && this.ftpStopNtc <= this.dimStopMax) {
          this.inputError['dimStop'] = false;
        }
      } else {
        this.inputError['dimStop'] = (num >= this.ftpStartNtc);
        // Constraint satisfied — also clear start's cross-field error if start is in range
        if (num < this.ftpStartNtc && this.ftpStartNtc >= this.dimStartMin && this.ftpStartNtc <= this.dimStartMax) {
          this.inputError['dimStart'] = false;
        }
      }
      this.updateTempLabels();
      this.pushFtpGraph();
    }

    this.recalcErrorState();
    this.checkDefaults();
    this.updateFormControl();
    if (!this.defaultColor) {
      this.checkboxState.enableOtpCheckbox();
      this.isIncludedInConfiguration = true;
    }
    this.sendErrorStateToParent();
    this.ref.detectChanges();
  }

  getSliderSafeValue(value: number, min: number, max: number): number {
    if (value === null || value === undefined || isNaN(value)) { return min; }
    if (value < min) { return min; }
    if (value > max) { return max; }
    return value;
  }

  onFtpCheckChange(event: Event): void {
    if (this.hasOtpValidationError) {
      return;
    }

    this.ignoreParentUpdate = true;
    const checked = (event.target as HTMLInputElement).checked;
    this.ftpEnabled = checked;
    this.dtpEnabled = !checked;
    if (!checked) {
      // FTP deselected — reset FTP to defaults
      this.ftpOption          = this.ftpOptionDefault;
      this.ftpStartNtc        = this.dimStartDefault;
      this.ftpStopNtc         = this.dimStopDefault;
      this.ftpStartNtcDisplay = this.dimStartDefault;
      this.ftpStopNtcDisplay  = this.dimStopDefault;
      this.inputError['dimStart'] = false;
      this.inputError['dimStop']  = false;
      const disabled = this.ftpOptionDefault === 'dlmFlex';
      this.ftpStartDimOptions = { ...this.ftpStartDimOptions, disabled };
      this.ftpStopDimOptions  = { ...this.ftpStopDimOptions,  disabled };
      this.pushFtpGraph();
    } else {
      // DTP deselected — reset DTP to defaults
      this.dtpOption = this.dtpOptionDefault;
      this.pushDtpGraph();
    }
    this.recalcErrorState();
    this.checkDefaults();
    this.updateFormControl();
    if (!this.defaultColor) {
      this.checkboxState.enableOtpCheckbox();
      this.isIncludedInConfiguration = true;
    }
    this.sendErrorStateToParent();
    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }

  onDtpCheckChange(event: Event): void {
    if (this.hasOtpValidationError) {
      return;
    }

    this.ignoreParentUpdate = true;
    const checked = (event.target as HTMLInputElement).checked;
    this.dtpEnabled = checked;
    this.ftpEnabled = !checked;
    if (!checked) {
      // DTP deselected — reset DTP to defaults
      this.dtpOption = this.dtpOptionDefault;
      this.pushDtpGraph();
    } else {
      // FTP deselected — reset FTP to defaults
      this.ftpOption          = this.ftpOptionDefault;
      this.ftpStartNtc        = this.dimStartDefault;
      this.ftpStopNtc         = this.dimStopDefault;
      this.ftpStartNtcDisplay = this.dimStartDefault;
      this.ftpStopNtcDisplay  = this.dimStopDefault;
      this.inputError['dimStart'] = false;
      this.inputError['dimStop']  = false;
      const disabled = this.ftpOptionDefault === 'dlmFlex';
      this.ftpStartDimOptions = { ...this.ftpStartDimOptions, disabled };
      this.ftpStopDimOptions  = { ...this.ftpStopDimOptions,  disabled };
      this.pushFtpGraph();
    }
    this.recalcErrorState();
    this.checkDefaults();
    this.updateFormControl();
    if (!this.defaultColor) {
      this.checkboxState.enableOtpCheckbox();
      this.isIncludedInConfiguration = true;
    }
    this.sendErrorStateToParent();
    this.ignoreParentUpdate = false;
  }

  onFtpOptionChange(value: string): void {
    this.ignoreParentUpdate = true;
    this.ftpOption = value;

    const isDlmFlex = value === 'dlmFlex';
    const disabled = isDlmFlex;

    if (isDlmFlex) {
      // Reset to defaults and disable sliders
      this.ftpStartNtc        = this.dimStartDefault;
      this.ftpStopNtc         = this.dimStopDefault;
      this.ftpStartNtcDisplay = this.dimStartDefault;
      this.ftpStopNtcDisplay  = this.dimStopDefault;
      this.inputError['dimStart'] = false;
      this.inputError['dimStop']  = false;
      this.pushFtpGraph();
    }

    this.ftpStartDimOptions = { ...this.ftpStartDimOptions, disabled };
    this.ftpStopDimOptions  = { ...this.ftpStopDimOptions,  disabled };

    this.recalcErrorState();
    this.checkDefaults();
    this.updateFormControl();
    if (!this.defaultColor) {
      this.checkboxState.enableOtpCheckbox();
      this.isIncludedInConfiguration = true;
    }
    this.sendErrorStateToParent();
    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }

  onDtpOptionChange(value: string): void {
    this.ignoreParentUpdate = true;
    this.dtpOption = value;
    this.pushDtpGraph();
    this.checkDefaults();
    this.updateFormControl();
    if (!this.defaultColor) {
      this.checkboxState.enableOtpCheckbox();
      this.isIncludedInConfiguration = true;
    }
    this.sendErrorStateToParent();
    this.ignoreParentUpdate = false;
  }

  private checkDefaults(): void {
    this.defaultColor = (
      this.ftpEnabled  === (this.otpModeDefault === 1) &&
      this.ftpOption   === this.ftpOptionDefault &&
      this.dtpOption   === this.dtpOptionDefault &&
      this.ftpStartNtc === this.dimStartDefault &&
      this.ftpStopNtc  === this.dimStopDefault
    );
    this.featureDefaults.setOtpDefaultColor(this.defaultColor);
  }

  resetToDefaults(): void {
    this.isResetting = true;
    this.hasOtpValidationError = false;

    this.ftpEnabled         = (this.otpModeDefault === 1);
    this.dtpEnabled         = (this.otpModeDefault === 0);
    this.ftpOption          = this.ftpOptionDefault;
    this.dtpOption          = this.dtpOptionDefault;
    this.ftpStartNtc        = this.dimStartDefault;
    this.ftpStopNtc         = this.dimStopDefault;
    this.ftpStartNtcDisplay = this.dimStartDefault;
    this.ftpStopNtcDisplay  = this.dimStopDefault;
    this.ftpStartTempLabel  = this.ntcToTemp(this.dimStartDefault);
    this.ftpStopTempLabel   = this.ntcToTemp(this.dimStopDefault);
    this.inputError         = {};
    this.hasInputError      = false;
    this.otpDisabledButton  = false;
    this.defaultColor       = true;

    // Re-apply disabled state based on default option
    const disableOnReset = this.ftpOptionDefault === 'dlmFlex';
    this.ftpStartDimOptions = { ...this.ftpStartDimOptions, disabled: disableOnReset };
    this.ftpStopDimOptions  = { ...this.ftpStopDimOptions,  disabled: disableOnReset };

    this.featureDefaults.setOtpDefaultColor(true);
    this.inputErrors.setOtpInputError.next(false);
    this.pushFtpGraph();
    this.pushDtpGraph();
    this.updateFormControl();
    this.ref.detectChanges();

    setTimeout(() => { this.isResetting = false; }, 150);
  }

  private recalcErrorState(): void {
    this.otpDisabledButton = this.hasOtpValidationError;
    if (!this.otpDisabledButton) {
      for (const type in this.inputError) {
        if (this.inputError[type]) {
          this.otpDisabledButton = true;
          break;
        }
      }
    }
    this.hasInputError = this.otpDisabledButton;
  }

  private sendErrorStateToParent(): void {
    if (this.isIncludedInConfiguration) {
      this.inputErrors.setOtpInputError.next(this.otpDisabledButton);
    } else {
      this.inputErrors.setOtpInputError.next(false);
    }
  }

  private updateFormControl(): void {
    if (!this.formControl) { return; }
    // Only emit the fields relevant to the currently selected mode.
    // The backend treats FTP (SelectedNTC/DimStart/DimStop) and DTP (DTP)
    // as mutually exclusive, so persisting both branches together triggers
    // a `1203 mutually exclusive` validation error at save time.
    const value: LegacyAny = { OTPMode: this.ftpEnabled ? 1 : 0 };
    if (this.ftpEnabled) {
      value.SelectedNTC = this.ftpOption === 'dlmFlex' ? 'DLM Flex Compatible' : 'Custom NTC';
      value.DimStart    = this.ftpStartNtc;
      value.DimStop     = this.ftpStopNtc;
    } else {
      value.DTP = this.dtpOption === '92to97' ? '92-97' : '83-87';
    }
    this.formControl.setValue(value);
    this.setConfigurationData(value);
  }

  private setConfigurationData(value: any): void {
    if (value != null) {
      const otpObj = Object.assign({}, value);
      this.appStateService.setFeatureData('OTP', otpObj);
    }
  }

  private pushFtpGraph(): void {
    this.graphState.otpFtpGraphData.next({
      dimStart: this.ftpStartNtc,
      dimStop: this.ftpStopNtc,
      minDimLevel: this.dimStopMinDimLevel,
    });
  }

  private pushDtpGraph(): void {
    const is9297 = this.dtpOption === '92to97';
    this.graphState.otpDtpGraphData.next({ dimStart: is9297 ? 92 : 83, dimStop: is9297 ? 97 : 87 });
  }

  // NTC Beta model — NCP18WF104F12RB (100kΩ at 25°C, B25/85 = 4260 K)
  private readonly NTC_R0   = 100000; // Ω at 25°C
  private readonly NTC_T0   = 298.15; // 25°C in Kelvin
  private readonly NTC_BETA = 4260;   // B(25-85) from datasheet

  ntcToTemp(ohm: number): string {
    if (!ohm || ohm <= 0) { return '--'; }
    const tKelvin = 1 / (1 / this.NTC_T0 + Math.log(ohm / this.NTC_R0) / this.NTC_BETA);
    return Math.round(tKelvin - 273.15).toString();
  }

  private updateTempLabels(): void {
    if (this.ftpOption === 'dlmFlex') {
      this.ftpStartTempLabel = this.ntcToTemp(this.ftpStartNtc);
      this.ftpStopTempLabel  = this.ntcToTemp(this.ftpStopNtc);
    }
  }

  private getNumber(value: any, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  numberOnly(event: KeyboardEvent): void {
    const key = event.key;
    if (!/[0-9]/.test(key) && key !== 'Backspace' && key !== 'Tab' && key !== 'ArrowLeft' && key !== 'ArrowRight') {
      event.preventDefault();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
